import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../components/AuthContext';
import api from '../api/client';
import CareTeamDisplay from '../components/CareTeamDisplay';
import {
  Users, Stethoscope, Mail, ChevronRight, ChevronDown, Search, MessageSquare, LogOut as LeaveIcon, X,
  Shield, CheckCircle, ChevronUp, UserCheck, SlidersHorizontal, Save, Loader2
} from 'lucide-react';
import Pagination from '../components/Pagination';
import toast from 'react-hot-toast';

const SCOPE_GROUPS = [
  {
    label: 'Clinical Data',
    fields: [
      { key: 'symptoms',     label: 'Symptoms',      desc: 'Symptom reports and history' },
      { key: 'conditions',   label: 'Conditions',    desc: 'Active and historical conditions' },
      { key: 'medications',  label: 'Medications',   desc: 'Current and past medications' },
      { key: 'allergies',    label: 'Allergies',     desc: 'Allergy and adverse reaction records' },
      { key: 'vitals',       label: 'Vitals',        desc: 'Vital sign measurements' },
      { key: 'labResults',   label: 'Lab Results',   desc: 'Laboratory and diagnostic test results' },
    ]
  },
  {
    label: 'Care & Events',
    fields: [
      { key: 'careEvents',   label: 'Care Events',   desc: 'Hospital visits and care transitions' },
    ]
  },
  {
    label: 'Sensitive Records',
    sensitive: true,
    fields: [
      { key: 'mentalHealth',   label: 'Mental Health',  desc: 'Mental health and psychiatric records' },
      { key: 'socialHistory',  label: 'Social History', desc: 'Lifestyle, substance use, social context' },
    ]
  },
  {
    label: 'Access Controls',
    fields: [
      { key: 'realTimeUpdates', label: 'Real-time Updates', desc: 'Live alerts when new data is recorded' },
      { key: 'allowExport',     label: 'Allow Data Export',  desc: 'Doctor can download your records' },
      { key: 'allowSharing',    label: 'Allow Re-sharing',   desc: 'Doctor can share data with other providers' },
    ]
  },
];
const SCOPE_FIELDS = SCOPE_GROUPS.flatMap(g => g.fields);

/**
 * CareTeamPage — Patient: view assigned doctors. Doctor: view patient list.
 */
const CareTeamPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isDoctor = user?.role === 'DOCTOR';
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState(null);
  const [conversationPicker, setConversationPicker] = useState(null); // { doctorName, matches: [] }
  const [activeTab, setActiveTab] = useState('team'); // 'team' | 'privacy'
  const [consents, setConsents] = useState([]);
  const [consentLoading, setConsentLoading] = useState(false);
  const [expandedConsent, setExpandedConsent] = useState(null);
  const [dataSharingOn, setDataSharingOn] = useState(true);
  const [editingScope, setEditingScope] = useState(null);   // doctorId being edited
  const [scopeDraft, setScopeDraft] = useState({});          // { [key]: bool }
  const [savingScope, setSavingScope] = useState(false);
  const ITEMS_PER_PAGE = 10;

  useEffect(() => {
    const fetch = async () => {
      try {
        setLoading(true);
        if (isDoctor) {
          // Doctor sees patients from their care team assignments
          const patientsRes = await api.get('/patient-profile/my-patients').catch(() => null);
          const patients = patientsRes?.data?.data || [];
          setItems(patients.map(p => ({
            id: p.id,
            name: p.name || 'Unknown Patient',
            email: p.email || '',
            phone: p.phone || '',
            role: p.role || 'specialist',
            addedAt: p.addedAt,
            conditions: p.conditions || 0,
            medications: p.medications || 0,
            hasConsent: p.hasConsent,
            type: 'patient'
          })));
        } else {
          // Patient sees care team
          const res = await api.get('/patient-profile/my-care-team').catch(() => null);
          const data = res?.data?.data;
          const team = [];
          // Primary care provider
          if (data?.primary) {
            team.push({
              id: data.primary._id,
              name: data.primary.name || 'Doctor',
              email: data.primary.email || '',
              specialty: data.primary.specialization || 'General Practice',
              role: 'primary',
              type: 'doctor'
            });
          }
          // Other care team members
          if (Array.isArray(data?.members)) {
            data.members.forEach(m => {
              team.push({
                id: m._id,
                name: m.name || 'Doctor',
                email: m.email || '',
                specialty: m.specialization || m.role || 'General Practice',
                role: m.role,
                type: 'doctor'
              });
            });
          }
          setItems(team);
        }
      } catch (err) {
        console.error('Failed to fetch care team:', err);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  // Load consent data when privacy tab is selected
  useEffect(() => {
    if (activeTab !== 'privacy' || isDoctor) return;
    const loadConsents = async () => {
      try {
        setConsentLoading(true);
        const [consentsRes, consentStatusRes] = await Promise.all([
          api.get('/consent/my-consents').catch(() => ({ data: { data: [] } })),
          api.get('/transparency/consent-status').catch(() => null)
        ]);
        setConsents(consentsRes.data?.data || []);
        setDataSharingOn(consentStatusRes?.data?.data?.dataSharingConsent !== false);
      } catch (err) {
        console.error('Failed to load consents:', err);
      } finally {
        setConsentLoading(false);
      }
    };
    loadConsents();
  }, [activeTab, isDoctor]);

  const handleRevokeConsent = async (consentId) => {
    if (!confirm('Revoke this doctor\'s access to your data?')) return;
    try {
      await api.post(`/consent/revoke/${consentId}`);
      toast.success('Data access revoked');
      setConsents(prev => prev.map(c => c._id === consentId ? { ...c, status: 'revoked' } : c));
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to revoke access');
    }
  };

  const getConsentForDoctor = (doctorId) =>
    consents.find(c => (c.doctorId?._id || c.doctorId) === doctorId && c.status === 'active');

  const openScopeEditor = (doctor, consent) => {
    setEditingScope(doctor.id);
    const base = {};
    SCOPE_FIELDS.forEach(f => {
      // sensitive fields default off if no existing consent
      const sensitiveOff = ['mentalHealth', 'socialHistory', 'allowExport', 'allowSharing'];
      const defaultVal = consent
        ? (consent.scope?.[f.key] !== false)
        : !sensitiveOff.includes(f.key);
      base[f.key] = defaultVal;
    });
    base.historicalDataMonths = consent?.scope?.historicalDataMonths ?? 12;
    setScopeDraft(base);
  };

  const handleSaveScope = async (doctor) => {
    setSavingScope(true);
    try {
      const consent = getConsentForDoctor(doctor.id);
      if (consent) {
        // Modify existing consent scope
        await api.put(`/consent/modify/${consent._id}`, {
          scope: scopeDraft,
          reason: 'Patient updated access scope'
        });
        setConsents(prev => prev.map(c =>
          c._id === consent._id ? { ...c, scope: { ...c.scope, ...scopeDraft } } : c
        ));
      } else {
        // Grant new scoped consent for implicit care team member
        const res = await api.post('/consent/grant', {
          doctorId: doctor.id,
          scope: scopeDraft,
          purpose: 'Patient customized care team access'
        });
        if (res.data?.data) {
          setConsents(prev => [...prev, res.data.data]);
        }
      }
      toast.success('Access permissions updated');
      setEditingScope(null);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to update permissions');
    } finally {
      setSavingScope(false);
    }
  };

  const handleMessage = async (doctorId, doctorName) => {
    try {
      const res = await api.get('/symptoms/me?page=1&limit=50').catch(() => null);
      const symptoms = res?.data?.data || [];
      const matches = symptoms.filter(s =>
        s.responses?.some(r => r.doctorId === doctorId || r.doctorId?._id === doctorId)
      );
      if (matches.length === 1) {
        navigate(`/chat/${matches[0]._id}`);
      } else if (matches.length > 1) {
        setConversationPicker({ doctorName, matches });
      } else {
        toast('Report a health issue first to start a conversation with your doctor.', { icon: 'ℹ️' });
        navigate('/report');
      }
    } catch {
      navigate('/messages');
    }
  };

  const handleLeaveDoctor = async (doctorId, doctorName) => {
    if (!confirm(`Remove ${doctorName} from your care team? This will revoke their access to your data.`)) return;
    try {
      await api.post(`/patient-profile/leave-doctor/${doctorId}`);
      toast.success(`${doctorName} removed from your care team`);
      setItems(prev => prev.filter(i => i.id !== doctorId));
    } catch (err) {
      toast.error('Failed to leave care team');
    }
  };

  const filtered = items.filter(item =>
    !searchTerm || item.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
  const paged = filtered.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);

  return (
    <div className="space-y-6 py-6">
      <div>
        <h2 className="font-headline text-2xl font-extrabold text-slate-900 tracking-tight">
          {isDoctor ? 'My Patients' : 'My Care Team'}
        </h2>
        <p className="text-slate-500 text-sm mt-1">
          {isDoctor ? 'View and manage your assigned patients' : 'Your assigned healthcare providers'}
        </p>
      </div>

      {/* New Care Team Display with Doctor Assignment */}
      {!isDoctor && user?.id && <CareTeamDisplay patientId={user?.id} />}

      {/* Patient tabs */}
      {!isDoctor && (
        <div className="flex gap-1 bg-slate-100 p-1 rounded-xl">
          {[
            { key: 'team', label: 'Doctors', icon: Users },
            { key: 'privacy', label: 'Data Privacy', icon: Shield },
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-bold transition-all ${
                activeTab === tab.key ? 'bg-white text-primary shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>
      )}

      {/* Privacy Tab Content */}
      {!isDoctor && activeTab === 'privacy' ? (
        <div className="space-y-4">
          {consentLoading ? (
            <div className="space-y-3">
              {[1, 2].map(i => <div key={i} className="h-24 rounded-2xl animate-pulse bg-slate-100" />)}
            </div>
          ) : items.length === 0 ? (
            <div className="bg-white rounded-2xl p-12 text-center border border-outline-variant/10">
              <Shield className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <h3 className="font-bold text-lg text-slate-900 mb-1">No doctors in your care team</h3>
              <p className="text-sm text-slate-500">Doctors on your care team can view your health records.</p>
            </div>
          ) : (
            <>
              {/* Summary banner */}
              <div className={`rounded-2xl p-4 border flex items-center gap-3 ${
                dataSharingOn ? 'bg-white border-outline-variant/10' : 'bg-amber-50 border-amber-200'
              }`}>
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${
                  dataSharingOn ? 'bg-emerald-50' : 'bg-amber-100'
                }`}>
                  <UserCheck className={`w-4 h-4 ${dataSharingOn ? 'text-emerald-600' : 'text-amber-600'}`} />
                </div>
                <div>
                  <p className="text-sm font-bold text-slate-900">{items.length} doctor{items.length !== 1 ? 's' : ''} in your care team</p>
                  <p className="text-xs text-slate-500">
                    {dataSharingOn
                      ? 'Default access on — care team members can view your health records'
                      : 'Default access off — only doctors with explicit permissions can view your data'}
                  </p>
                </div>
              </div>

              {/* Doctor cards */}
              <div className="space-y-3">
                {items.map(doctor => {
                  const consent = getConsentForDoctor(doctor.id);
                  const isExp = expandedConsent === doctor.id;
                  return (
                    <div key={doctor.id} className="bg-white rounded-2xl border border-outline-variant/10 overflow-hidden">
                      <button
                        onClick={() => setExpandedConsent(isExp ? null : doctor.id)}
                        className="w-full p-4 flex items-center gap-4 text-left"
                      >
                        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0 text-primary font-extrabold text-sm">
                          {(doctor.name || 'D').split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-bold text-sm text-slate-900 break-words">{doctor.name}</h4>
                          <p className="text-xs text-slate-500">{doctor.specialty}</p>
                        </div>
                        <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-50 text-emerald-600">
                          Has Access
                        </span>
                        {isExp ? <ChevronUp className="w-4 h-4 text-slate-300" /> : <ChevronDown className="w-4 h-4 text-slate-300" />}
                      </button>

                      {isExp && (
                        <div className="px-4 pb-4 border-t border-slate-100 pt-3 space-y-4">

                          {editingScope === doctor.id ? (
                            /* ── Scope editor ── */
                            <div className="space-y-4">
                              <p className="text-xs font-bold text-slate-500 uppercase tracking-wide">Manage Access Permissions</p>

                              {SCOPE_GROUPS.map(group => (
                                <div key={group.label}>
                                  <p className={`text-[10px] font-bold uppercase tracking-widest mb-2 ${group.sensitive ? 'text-amber-600' : 'text-slate-400'}`}>{group.label}</p>
                                  <div className="space-y-1">
                                    {group.fields.map(f => (
                                      <div key={f.key} className="flex items-center justify-between py-2 border-b border-slate-50 last:border-0">
                                        <div className="min-w-0">
                                          <p className="text-xs font-semibold text-slate-800">{f.label}</p>
                                          <p className="text-xs text-slate-400">{f.desc}</p>
                                        </div>
                                        <button
                                          onClick={() => setScopeDraft(p => ({ ...p, [f.key]: !p[f.key] }))}
                                          className={`relative w-9 h-5 rounded-full transition-colors duration-200 shrink-0 ml-3 ${
                                            scopeDraft[f.key] ? (group.sensitive ? 'bg-amber-500' : 'bg-primary') : 'bg-slate-300'
                                          }`}
                                        >
                                          <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${
                                            scopeDraft[f.key] ? 'translate-x-4' : 'translate-x-0'
                                          }`} />
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              ))}

                              {/* Data history window */}
                              <div>
                                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">Data History Window</p>
                                <div className="space-y-2">
                                  <div className="flex items-center justify-between">
                                    <p className="text-xs text-slate-600">How far back can this doctor see?</p>
                                    <span className="text-xs font-bold text-primary">
                                      {scopeDraft.historicalDataMonths === 0 ? 'Current only' : `${scopeDraft.historicalDataMonths} months`}
                                    </span>
                                  </div>
                                  <input
                                    type="range" min="0" max="60" step="6"
                                    value={scopeDraft.historicalDataMonths ?? 12}
                                    onChange={e => setScopeDraft(p => ({ ...p, historicalDataMonths: parseInt(e.target.value) }))}
                                    className="w-full accent-primary"
                                  />
                                  <div className="flex justify-between text-[10px] text-slate-400">
                                    <span>Current only</span><span>1 year</span><span>2 years</span><span>5 years</span>
                                  </div>
                                </div>
                              </div>
                              <div className="flex gap-2 pt-1">
                                <button
                                  onClick={() => handleSaveScope(doctor)}
                                  disabled={savingScope}
                                  className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-primary text-white rounded-xl text-xs font-bold disabled:opacity-50"
                                >
                                  {savingScope ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                                  {savingScope ? 'Saving…' : 'Save Permissions'}
                                </button>
                                <button
                                  onClick={() => setEditingScope(null)}
                                  className="px-4 py-2.5 bg-slate-100 text-slate-600 rounded-xl text-xs font-bold hover:bg-slate-200 transition-colors"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            /* ── Read-only scope summary ── */
                            <div className="space-y-3">
                              {SCOPE_GROUPS.map(group => (
                                <div key={group.label}>
                                  <p className={`text-[10px] font-bold uppercase tracking-widest mb-1.5 ${group.sensitive ? 'text-amber-600' : 'text-slate-400'}`}>{group.label}</p>
                                  <div className="grid grid-cols-2 gap-1.5">
                                    {group.fields.map(f => {
                                      const sensitiveOff = ['mentalHealth', 'socialHistory', 'allowExport', 'allowSharing'];
                                      const hasAccess = consent ? consent.scope?.[f.key] !== false : !sensitiveOff.includes(f.key);
                                      return (
                                        <div key={f.key} className="flex items-center gap-1.5 text-xs">
                                          {hasAccess
                                            ? <CheckCircle className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                                            : <X className="w-3.5 h-3.5 text-slate-300 shrink-0" />
                                          }
                                          <span className={hasAccess ? 'text-slate-700' : 'text-slate-400'}>{f.label}</span>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              ))}
                              {consent?.scope?.historicalDataMonths !== undefined && (
                                <p className="text-xs text-slate-500">
                                  History window: <span className="font-bold text-slate-700">{consent.scope.historicalDataMonths === 0 ? 'Current only' : `${consent.scope.historicalDataMonths} months`}</span>
                                </p>
                              )}
                              {consent && (
                                <p className="text-xs text-slate-400">
                                  Consent granted {new Date(consent.grantedAt).toLocaleDateString()}
                                </p>
                              )}

                              <button
                                onClick={() => openScopeEditor(doctor, consent)}
                                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-primary/5 text-primary rounded-xl text-xs font-bold hover:bg-primary/10 transition-colors border border-primary/10"
                              >
                                <SlidersHorizontal className="w-3.5 h-3.5" />
                                Manage Access Permissions
                              </button>
                            </div>
                          )}

                          <div className="pt-1 border-t border-slate-100">
                            <button
                              onClick={() => handleLeaveDoctor(doctor.id, doctor.name)}
                              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-red-50 text-red-600 rounded-xl text-xs font-bold hover:bg-red-100 transition-colors"
                            >
                              <LeaveIcon className="w-3.5 h-3.5" />
                              Remove from Care Team
                            </button>
                            <p className="text-xs text-slate-400 text-center mt-1">This will revoke all access to your health data</p>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* How it works */}
              <div className="bg-blue-50 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <Shield className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-xs font-bold text-slate-700 mb-1">How data access works</p>
                    <ul className="text-xs text-slate-600 space-y-0.5">
                      <li>• Doctors on your care team can view your health records</li>
                      <li>• Removing a doctor revokes all their access immediately</li>
                      <li>• All access activity is logged for your records</li>
                    </ul>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      ) : (
      <>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
        <input
          type="text"
          placeholder={isDoctor ? 'Search patients...' : 'Search doctors...'}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-3 bg-white border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
        />
      </div>

      {/* List */}
      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map(i => <div key={i} className="h-20 rounded-2xl animate-pulse bg-slate-100" />)}
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 text-center border border-outline-variant/10">
          <Users className="w-12 h-12 mx-auto mb-3 text-slate-300" />
          <h3 className="font-bold text-lg text-slate-900 mb-1">
            {isDoctor ? 'No patients found' : 'No care team members'}
          </h3>
          <p className="text-sm text-slate-500">
            {isDoctor ? 'No patients are assigned to you yet.' : 'No doctors are assigned to your care yet.'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {paged.map((item) => {
            const isExpanded = expandedId === item.id;
            return (
              <div key={item.id} className="bg-white rounded-2xl border border-outline-variant/10 hover:shadow-md transition-all overflow-hidden">
                <button
                  onClick={() => isDoctor ? navigate(`/patient/${item.id}`) : setExpandedId(isExpanded ? null : item.id)}
                  className="w-full p-5 flex items-center gap-4 text-left"
                >
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 font-extrabold text-sm ${
                    isDoctor ? 'bg-secondary/10 text-secondary' : 'bg-primary/10 text-primary'
                  }`}>
                    {(item.name || '?').split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h4 className="font-bold text-sm text-slate-900 break-words">{item.name}</h4>
                      {!isDoctor && item.role && (
                        <span className={`px-2 py-0.5 rounded-full text-xs font-bold capitalize ${
                          item.role === 'primary' ? 'bg-primary/10 text-primary' : 'bg-slate-100 text-slate-600'
                        }`}>
                          {item.role}
                        </span>
                      )}
                      {isDoctor && item.hasConsent !== undefined && (
                        <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                          item.hasConsent ? 'bg-emerald-100 text-emerald-700' : 'bg-red-50 text-red-500'
                        }`}>
                          {item.hasConsent ? 'Consent' : 'No Consent'}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {isDoctor ? item.email : item.specialty}
                    </p>
                  </div>
                  {isDoctor && item.riskScore > 0 && (
                    <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                      item.riskScore >= 70 ? 'bg-error/10 text-error' :
                      item.riskScore >= 40 ? 'bg-amber-100 text-amber-700' :
                      'bg-emerald-100 text-emerald-700'
                    }`}>
                      Risk: {item.riskScore}
                    </span>
                  )}
                  {isDoctor ? (
                    <ChevronRight className="w-5 h-5 text-slate-300 shrink-0" />
                  ) : (
                    <ChevronDown className={`w-5 h-5 text-slate-300 shrink-0 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                  )}
                </button>

                {/* Expanded detail for patient view */}
                {!isDoctor && isExpanded && (
                  <div className="px-5 pb-5 pt-0 border-t border-slate-100">
                    <div className="flex flex-col gap-3 pt-4">
                      {item.email && (
                        <div className="flex items-center gap-2 text-sm text-slate-600">
                          <Mail className="w-4 h-4 text-slate-500" />
                          <span className="break-all">{item.email}</span>
                        </div>
                      )}
                      {item.specialty && (
                        <div className="flex items-center gap-2 text-sm text-slate-600">
                          <Stethoscope className="w-4 h-4 text-slate-500" />
                          <span>{item.specialty}</span>
                        </div>
                      )}
                      <div className="flex flex-wrap gap-2 mt-2">
                        <button
                          onClick={() => handleMessage(item.id, item.name)}
                          className="flex items-center gap-2 px-4 py-2 bg-primary/10 text-primary rounded-xl text-xs font-bold hover:bg-primary/20 transition-colors"
                        >
                          <MessageSquare className="w-4 h-4" />
                          Message
                        </button>
                        <button
                          onClick={() => handleLeaveDoctor(item.id, item.name)}
                          className="flex items-center gap-2 px-4 py-2 bg-red-50 text-red-600 rounded-xl text-xs font-bold hover:bg-red-100 transition-colors"
                        >
                          <LeaveIcon className="w-4 h-4" />
                          Remove from Care Team
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />

      </>
      )}

      {/* Conversation picker modal */}
      {conversationPicker && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40" onClick={() => setConversationPicker(null)}>
          <div
            className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl p-6 max-h-[70vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-bold text-lg text-slate-900">Choose conversation</h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  You have {conversationPicker.matches.length} open threads with {conversationPicker.doctorName}
                </p>
              </div>
              <button onClick={() => setConversationPicker(null)} className="p-2 hover:bg-slate-100 rounded-xl transition-colors">
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            <div className="space-y-2">
              {conversationPicker.matches.map(s => (
                <button
                  key={s._id}
                  onClick={() => { setConversationPicker(null); navigate(`/chat/${s._id}`); }}
                  className="w-full p-4 bg-slate-50 hover:bg-primary/5 rounded-xl text-left transition-colors flex items-center gap-3"
                >
                  <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                    <MessageSquare className="w-4 h-4 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-sm text-slate-900 break-words">{s.title || 'Untitled'}</p>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {s.severity && <span className="capitalize">{s.severity}</span>}
                      {s.createdAt && <> · {new Date(s.createdAt).toLocaleDateString()}</>}
                    </p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-300 shrink-0" />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CareTeamPage;
